import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useRef, useState } from "react";
import ReactAsyncSelect from "react-select/async";

export default function LocationInput({
  className,
  id,
  name,
  value,
  placeholder,
  onInputClear,
  onPlaceChange
}) {
  const places = useMapsLibrary('places');
  const [isLoading, setIsLoading] = useState(false);
  const timeout = useRef(null);

  const handleRequest = async (inputValue) => {
    try {
      if (!places) {
        return [];
      }

      setIsLoading(true);

      const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: inputValue,
        sessionToken: new places.AutocompleteSessionToken()
      });

      const mappedSuggestions = [];

      for (const suggestion of response.suggestions) {
        const place = suggestion.placePrediction.toPlace();

        await place.fetchFields({
          fields: ["id", "location", "formattedAddress"],
        });

        mappedSuggestions.push({
          value: place.id,
          label: place.formattedAddress,
          address: place.formattedAddress,
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        });
      }

      return mappedSuggestions;

    } finally {
      setIsLoading(false);
    }
  }

  const handleLoadOptions = (inputValue) => {
    return new Promise(async (resolve) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      timeout.current = setTimeout(() => {
        resolve(handleRequest(inputValue));
      }, 300);
    })
  }

  const handleInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === "input-change" && actionMeta.prevInputValue !== "" && inputValue === "") {
      onInputClear && onInputClear();
    }
  }

  const handleChange = (placeSuggestion) => {
    onPlaceChange && onPlaceChange(placeSuggestion);
  }

  return (
    <ReactAsyncSelect
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderRadius: "999px",
          border: "2px solid var(--color-black)",
          ":hover": {
            border: "2px solid var(--color-black)"
          },
          boxShadow: "none"
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          padding: 0,
          margin: 0,
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          margin: 0,
          padding: "6px 16px"
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          padding: "6px 16px"
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          margin: 0,
          padding: "6px 16px"
        }),
        indicatorSeparator: (baseStyles) => ({
          ...baseStyles,
          display: "none"
        })
      }}
      id={ id }
      name={ name }
      defaultInputValue={ value }
      placeholder={ placeholder }
      loadOptions={ handleLoadOptions }
      cacheOptions
      defaultOptions
      isSearchable
      isClearable
      isLoading={ isLoading }
      onInputChange={ handleInputChange }
      onChange={ handleChange }
      className={ className }
    />
  );
}
